import {Injectable} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {BehaviorSubject, Observable} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {FlatField} from '../model/flat-field';
import {Field} from '../model/field';
import {EformSharedFieldsService} from './eform-shared-fields.service';
import {EformDuplicableFieldsService} from './eform-duplicable-fields.service';
import {EformFlatFieldsService} from './eform-flat-fields.service';
import {DuplicableSectionEnum} from '../enum/duplicable-sections.enum';

/**
 * Service de nettoyage des champs interdits dans les formulaires eForm.
 *
 * Ce service gère le nettoyage automatique des champs qui ont une forbiddenConditionV2
 * et qui sont interdits selon l'évaluation des conditions.
 *
 * @version 1.0
 */
@Injectable({
  providedIn: 'root'
})
export class EformFieldCleanupService {
  /** Cache des métadonnées du formulaire (Field[]) chargées depuis les assets */
  private formMetadata: any = null;

  /** Cache des champs extraits avec forbiddenConditionV2 */
  private cachedFieldsWithConditions: Field[] = null;


  /** Nom du formulaire courant (ex: E_FORM_29) */
  private currentFormName: string = null;

  /** Map des éléments duplicables à reconstituer pour le nettoyage */
  private duplicableElementsToCleanup = new Map<string, Map<string, null>>();

  /** Référence à la fonction de récupération des flatFields (injectée) */
  private getFlatFieldsValue: () => Map<string, FlatField[]> = () => new Map();
  private setFlatFields: (value: Map<string, FlatField[]>) => void = () => {};
  private scanFormGroupToFlatFields: (group: FormGroup, parentPath: string, flatFieldsMap: Map<string, FlatField[]>) => void = () => {};

  /** Référence à la fonction d'évaluation des conditions (injectée) */
  private evaluateConditions: (condition: any, elementId: string, repeatablePathForDuplicableElement: string) => boolean = () => false;

  constructor(
    private http: HttpClient,
    private eformSharedFieldsService: EformSharedFieldsService,
    private eformDuplicableFieldsService: EformDuplicableFieldsService,
    private eformFlatFieldsService: EformFlatFieldsService
  ) {
  }

  /**
   * Configure les fonctions d'accès aux flatFields
   */
  setFlatFieldsAccessors(
    getValue: () => Map<string, FlatField[]>,
    setValue: (value: Map<string, FlatField[]>) => void,
    scanFormGroup: (group: FormGroup, parentPath: string, flatFieldsMap: Map<string, FlatField[]>) => void
  ): void {
    this.getFlatFieldsValue = getValue;
    this.setFlatFields = setValue;
    this.scanFormGroupToFlatFields = scanFormGroup;
  }

  /**
   * Configure la fonction d'évaluation des conditions
   */
  setConditionEvaluator(evaluator: (condition: any, elementId: string, repeatablePathForDuplicableElement: string) => boolean): void {
    this.evaluateConditions = evaluator;
  }

  /**
   * Définit le nom du formulaire courant pour charger ses métadonnées
   */
  setCurrentFormName(formName: string): void {
    this.currentFormName = formName;
  }

  /**
   * Retourne les éléments duplicables qui doivent être nettoyés avec info sur mandatory.
   */
  getCleanedDuplicableElements(): Array<{
    type: string,
    index: number,
    fieldsToClean: Map<string, { mandatory: boolean }>
  }> {
    const result: Array<{ type: string, index: number, fieldsToClean: Map<string, { mandatory: boolean }> }> = [];

    if (this.duplicableElementsToCleanup.size === 0) {
      return result;
    }

    this.duplicableElementsToCleanup.forEach((fieldsToClean, key) => {
      const [type, indexStr] = key.split('_');
      const index = parseInt(indexStr, 10);

      const enrichedFieldsToClean = new Map<string, { mandatory: boolean }>();

      fieldsToClean.forEach((_, fieldId) => {
        const fieldMetadata = this.findFieldMetadata(fieldId);
        const isMandatory = fieldMetadata?.mandatory && Array.isArray(fieldMetadata.mandatory) && fieldMetadata.mandatory.length > 0;
        enrichedFieldsToClean.set(fieldId, {mandatory: isMandatory});
      });

      result.push({
        type,
        index,
        fieldsToClean: enrichedFieldsToClean
      });
    });

    return result;
  }

  /**
   * Trouve les métadonnées d'un champ dans le cache
   */
  private findFieldMetadata(fieldId: string): Field | null {
    if (!this.cachedFieldsWithConditions) {
      return null;
    }
    return this.cachedFieldsWithConditions.find(f => f.id === fieldId) || null;
  }

  /**
   * Déclenche le nettoyage public des champs interdits
   */
  triggerCleanupForbiddenFields(formGroup: FormGroup): void {
    this.cleanupForbiddenFieldsWithConditions(formGroup);
  }

  /**
   * Nettoie les champs du formulaire qui ont une forbiddenConditionV2 et qui sont interdits.
   */
  cleanupForbiddenFieldsWithConditions(formGroup: FormGroup): void {
    // ✅ FIX 2026-01-09: Ne pas exécuter le cleanup si les flatFields ne sont pas prêts
    // ou si un élément duplicable est en cours de chargement
    if (!this.eformFlatFieldsService.eformFlatFieldsReady) {
      return;
    }
    if (this.eformDuplicableFieldsService.isReloadingDuplicableElementValue) {
      return;
    }
    // ✅ FIX 2026-02-18: Ne pas exécuter le cleanup pendant le chargement d'un avis lié
    // Cela évite d'effacer les champs conditionnels (lieu d'exécution, BT-106, etc.)
    // qui viennent d'être copiés depuis l'avis parent avant que BT-727 ne soit évalué
    if (this.eformDuplicableFieldsService.isLoadingAvisLieValue) {
      return;
    }

    if (this.cachedFieldsWithConditions) {
      this.evaluateAndCleanupFields(this.cachedFieldsWithConditions, formGroup);
      return;
    }

    if (!this.currentFormName) {
      return;
    }

    this.loadFormMetadata(this.currentFormName).subscribe(
      (formData) => {
        const allFields = this.extractAllFieldsFromMetadata(formData);
        this.cachedFieldsWithConditions = allFields;
        this.evaluateAndCleanupFields(allFields, formGroup);
      }
    );
  }

  /**
   * Charge les métadonnées du formulaire depuis les assets
   */
  private loadFormMetadata(formName: string): Observable<any> {
    if (this.formMetadata) {
      return new BehaviorSubject(this.formMetadata).asObservable();
    }

    const url = `assets/dynamic/creer/formSections/${formName}.json`;
    return this.http.get(url).pipe(
      take(1),
      map((data) => {
        this.formMetadata = data;
        return data;
      })
    );
  }

  /**
   * Extrait récursivement tous les champs (Field) depuis les métadonnées du formulaire.
   */
  private extractAllFieldsFromMetadata(formData: any): Field[] {
    const fields: Field[] = [];

    const extractFromSection = (obj: any) => {
      if (!obj || typeof obj !== 'object') return;

      // Extraire les champs avec forbiddenConditionV2
      if (obj.id && obj.forbiddenConditionV2) {
        fields.push(obj);
      }


      Object.keys(obj).forEach(key => {
        const value = obj[key];
        if (Array.isArray(value)) {
          value.forEach(item => extractFromSection(item));
        } else if (typeof value === 'object' && value !== null) {
          extractFromSection(value);
        }
      });
    };

    let sectionsSource: any[] = null;

    if (Array.isArray(formData.content)) {
      sectionsSource = formData.content;
    } else if (formData.content?.sections) {
      sectionsSource = formData.content.sections;
    } else if (formData.sections) {
      sectionsSource = formData.sections;
    }

    if (sectionsSource) {
      sectionsSource.forEach((section: any) => extractFromSection(section));
    }

    return fields;
  }

  /**
   * Évalue les conditions de tous les champs et nettoie ceux qui sont interdits
   */
  private evaluateAndCleanupFields(fields: Field[], formGroup: FormGroup): void {
    let cleanedCount = 0;

    this.duplicableElementsToCleanup.clear();

    fields.forEach((field) => {
      if (this.eformSharedFieldsService.isSharedLocationField(field.id)) {
        return;
      }

      // 🔧 FIX 2026-02-23: Utiliser DuplicableSectionEnum pour détecter les champs duplicables
      // au lieu d'une liste hardcodée de suffixes. Cela garantit que tous les types
      // d'éléments duplicables sont pris en compte automatiquement.
      // Ces champs ne doivent PAS être nettoyés par le cleanup car il n'a pas le contexte
      // repeatablePath nécessaire pour évaluer correctement leurs conditions.
      // Seul le composant form-input qui a le bon repeatablePath devrait gérer ces champs.
      if (this.isDuplicableField(field.id)) {
        return;
      }

      // 🔧 FIX 2026-01-13: Trouver TOUS les contrôles correspondants, pas seulement le premier
      // Cela permet de nettoyer toutes les instances d'un champ dans les tableaux imbriqués
      const allControls = this.findAllControlsByInputId(formGroup, field.id, field.listname);

      if (allControls.length > 0) {
        allControls.forEach((control, index) => {
          const elementId = `UNK_${index}`;
          const isForbidden = this.evaluateConditions(field.forbiddenConditionV2, elementId, '');


          // 🔧 FIX 2026-01-21: Nettoyer si la valeur n'est pas déjà null
          // Cela inclut les chaînes vides "" qui ont pu être mises par le bloc mandatory
          if (isForbidden && control.value !== null) {
            control.setValue(null, {emitEvent: false});
            control.markAsPristine();
            cleanedCount++;
          }
        });
      } else {
        const flatFieldEntry = this.getFlatFieldsValue().get(field.id);
        if (flatFieldEntry && flatFieldEntry.length > 0) {
          flatFieldEntry.forEach(flatField => {
            const hasValueInFlatFields = flatField.value != null && flatField.value !== '' && flatField.value !== 'null';

            if (hasValueInFlatFields) {
              const isForbidden = false; // Désactivé pour éviter l'erreur

              if (isForbidden) {
                const duplicableInfo = this.extractDuplicableInfoFromPath(flatField.path);

                if (duplicableInfo) {
                  const key = `${duplicableInfo.type}_${duplicableInfo.index}`;

                  if (!this.duplicableElementsToCleanup.has(key)) {
                    this.duplicableElementsToCleanup.set(key, new Map());
                  }
                  this.duplicableElementsToCleanup.get(key).set(field.id, null);
                }
              }
            }
          });
        }
      }
    });


    if (cleanedCount > 0) {
      setTimeout(() => {
        const currentFields = this.getFlatFieldsValue();
        const newFlatFields = new Map<string, FlatField[]>();

        // ✅ FIX: Conserver toutes les valeurs des champs duplicables dans flatFields
        // avant le rescan pour ne pas perdre les valeurs des éléments non chargés
        currentFields.forEach((fields, inputId) => {
          // Conserver les champs duplicables (ils seront marqués comme non affichés)
          const duplicableFields = fields
            .filter(field => field.isDuplicableField)
            .map(field => ({...field, isDisplayed: false}));

          // Conserver aussi les champs non duplicables cachés
          const hiddenNonDuplicableFields = fields.filter(field => !field.isDisplayed && !field.isDuplicableField);

          const fieldsToKeep = [...duplicableFields, ...hiddenNonDuplicableFields];
          if (fieldsToKeep.length > 0) {
            newFlatFields.set(inputId, fieldsToKeep);
          }
        });

        // Le scan met à jour/remplace les champs qui sont dans le formulaire actuel
        this.scanFormGroupToFlatFields(formGroup, '', newFlatFields);
        this.setFlatFields(newFlatFields);
      }, 0);
    }
  }

  /**
   * Extrait le type et l'index d'un élément duplicable depuis son path
   */
  private extractDuplicableInfoFromPath(path: string): { type: string, index: number } | null {
    if (!path) return null;

    const pattern1 = /^([A-Z]{3})_(\d+)$/;
    const match1 = path.match(pattern1);
    if (match1) {
      return {
        type: match1[1],
        index: parseInt(match1[2], 10)
      };
    }

    const duplicableTypes = ['organization', 'settledContract', 'tenderingParty', 'lotResult', 'lotTender'];
    for (const duplicableType of duplicableTypes) {
      const pattern = new RegExp(`${duplicableType}\\[(\\d+)\\]`);
      const match = path.match(pattern);
      if (match) {
        const typeMap: Record<string, string> = {
          'organization': 'ORG',
          'settledContract': 'CON',
          'tenderingParty': 'TPA',
          'lotResult': 'RES',
          'lotTender': 'TEN'
        };
        return {
          type: typeMap[duplicableType],
          index: parseInt(match[1], 10)
        };
      }
    }

    return null;
  }

  /**
   * Recherche récursivement un control dans un FormGroup par son inputId et optionnellement son listName.
   * Retourne aussi le FormArray parent et l'index pour permettre la suppression de l'élément.
   * @param group Le FormGroup à parcourir
   * @param inputId L'identifiant du champ à rechercher
   * @param listname Le listname optionnel pour différencier les champs partageant le même jsonPath
   * @param parentArray Le FormArray parent (pour la récursion)
   * @param indexInArray L'index dans le FormArray parent (pour la récursion)
   */
  private findControlByInputIdWithParent(
    group: any,
    inputId: string,
    listname?: string,
    parentArray?: any,
    indexInArray?: number
  ): { control: any, parentArray: any, indexInArray: number } | null {
    if (!group || typeof group !== 'object') return null;

    if (group.value && typeof group.value === 'object' && group.value.inputId === inputId) {
      // Vérifier le listName si spécifié
      if (listname) {
        const groupListName = group.get ? group.get('listName')?.value : null;
        if (groupListName && groupListName !== listname) {
          // Le listName ne correspond pas, continuer la recherche
        } else {
          return {
            control: group.get ? group.get('val') : null,
            parentArray: parentArray,
            indexInArray: indexInArray !== undefined ? indexInArray : -1
          };
        }
      } else {
        return {
          control: group.get ? group.get('val') : null,
          parentArray: parentArray,
          indexInArray: indexInArray !== undefined ? indexInArray : -1
        };
      }
    }

    if (group.controls) {
      for (const key of Object.keys(group.controls)) {
        const control = group.controls[key];

        // Vérifier si c'est un FormArray
        const isFormArray = Array.isArray(control?.controls);

        if (control && control.value && typeof control.value === 'object' && control.value.inputId === inputId) {
          // Vérifier le listName si spécifié
          if (listname) {
            const controlListName = control.get ? control.get('listName')?.value : null;
            if (controlListName && controlListName !== listname) {
              // Le listName ne correspond pas, continuer la recherche
              continue;
            }
          }
          return {
            control: control.get ? control.get('val') : null,
            parentArray: parentArray,
            indexInArray: indexInArray !== undefined ? indexInArray : -1
          };
        }

        if (control && typeof control === 'object') {
          if (isFormArray) {
            // Parcourir les éléments du FormArray
            for (let i = 0; i < control.controls.length; i++) {
              const found = this.findControlByInputIdWithParent(control.controls[i], inputId, listname, control, i);
              if (found) return found;
            }
          } else {
            const found = this.findControlByInputIdWithParent(control, inputId, listname, parentArray, indexInArray);
            if (found) return found;
          }
        }
      }
    }

    return null;
  }

  /**
   * Recherche récursivement un control dans un FormGroup par son inputId et optionnellement son listName
   * @param group Le FormGroup à parcourir
   * @param inputId L'identifiant du champ à rechercher
   * @param listname Le listname optionnel pour différencier les champs partageant le même jsonPath
   */
  private findControlByInputId(group: any, inputId: string, listname?: string): any {
    if (!group || typeof group !== 'object') return null;

    // Vérifier si ce groupe a un control 'inputId' avec la valeur recherchée
    const inputIdControl = group.get ? group.get('inputId') : null;
    const currentInputId = inputIdControl?.value;

    if (currentInputId === inputId) {
      // Vérifier le listName si spécifié
      if (listname) {
        const listNameControl = group.get ? group.get('listName') : null;
        const currentListName = listNameControl?.value;
        if (currentListName && currentListName !== listname) {
          // Le listName ne correspond pas, continuer la recherche
        } else {
          return group.get ? group.get('val') : null;
        }
      } else {
        return group.get ? group.get('val') : null;
      }
    }

    // Ancienne vérification pour compatibilité
    if (group.value && typeof group.value === 'object' && group.value.inputId === inputId) {
      if (listname) {
        const groupListName = group.get ? group.get('listName')?.value : null;
        if (groupListName && groupListName !== listname) {
          // Le listName ne correspond pas, continuer la recherche
        } else {
          return group.get ? group.get('val') : null;
        }
      } else {
        return group.get ? group.get('val') : null;
      }
    }

    if (group.controls) {
      for (const key of Object.keys(group.controls)) {
        const control = group.controls[key];

        if (control && typeof control === 'object') {
          const found = this.findControlByInputId(control, inputId, listname);
          if (found) return found;
        }
      }
    }

    return null;
  }

  /**
   * 🔧 FIX 2026-01-13: Recherche récursivement TOUS les controls dans un FormGroup par son inputId et optionnellement son listName
   * Retourne un tableau de tous les contrôles correspondants pour gérer les tableaux imbriqués.
   */
  private findAllControlsByInputId(group: any, inputId: string, listname?: string): any[] {
    const results: any[] = [];
    if (!group || typeof group !== 'object') return results;

    // Vérifier si ce groupe a un control 'inputId' avec la valeur recherchée
    const inputIdControl = group.get ? group.get('inputId') : null;
    const currentInputId = inputIdControl?.value;

    if (currentInputId === inputId) {
      // Vérifier le listName si spécifié
      let matchesListName = true;
      if (listname) {
        const listNameControl = group.get ? group.get('listName') : null;
        const currentListName = listNameControl?.value;
        if (currentListName && currentListName !== listname) {
          matchesListName = false;
        }
      }
      if (matchesListName) {
        const valControl = group.get ? group.get('val') : null;
        if (valControl) {
          results.push(valControl);
        }
      }
    }

    // Continuer la recherche dans les sous-groupes pour trouver d'autres instances
    if (group.controls) {
      for (const key of Object.keys(group.controls)) {
        const control = group.controls[key];
        if (control && typeof control === 'object') {
          const found = this.findAllControlsByInputId(control, inputId, listname);
          results.push(...found);
        }
      }
    }

    return results;
  }

  /**
   * 🔧 FIX 2026-02-23: Vérifie si un champ appartient à un élément duplicable
   * en utilisant DuplicableSectionEnum au lieu d'une liste hardcodée de suffixes.
   *
   * Cela garantit que tous les types d'éléments duplicables sont pris en compte
   * automatiquement quand l'enum est mis à jour, évitant ainsi les régressions.
   *
   * @param fieldId L'identifiant du champ (ex: BT-150-Contract, BT-137-Lot)
   * @returns true si le champ appartient à un élément duplicable
   */
  private isDuplicableField(fieldId: string): boolean {
    if (!fieldId) return false;

    // Récupérer toutes les valeurs de l'enum DuplicableSectionEnum
    const duplicableSections = Object.values(DuplicableSectionEnum)
      .filter(value => typeof value === 'string') as string[];

    // Vérifier si l'ID du champ se termine par un suffixe correspondant à une section duplicable
    // Les IDs de champs suivent le pattern: BT-XXX-SectionName ou OPT-XXX-SectionName
    // Exemple: BT-150-Contract, BT-137-Lot, OPT-321-Tender
    return duplicableSections.some(section => {
      // Vérifier le suffixe exact avec le tiret (ex: -Contract, -Lot, -Tender)
      if (fieldId.endsWith(`-${section}`)) {
        return true;
      }
      // Vérifier aussi les variantes composées (ex: -Organization-Company devient -Organization)
      // car certains champs peuvent avoir des suffixes partiels
      const baseSectionName = section.split('-')[0];
      if (baseSectionName !== section && fieldId.endsWith(`-${baseSectionName}`)) {
        return true;
      }
      return false;
    });
  }
}

